/**
 * LoginPromptModal - Global modal prompting guests to log in for wishlist
 */
import React, { memo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiHeart, FiLogIn } from 'react-icons/fi';

import styles from './LoginPromptModal.module.css';
import { useAppSelector, useAppDispatch } from '@/hooks';
import { selectShowLoginPrompt, closeLoginPrompt } from '@/store/slices/uiSlice';
import { ROUTES } from '@/config/routes';

const LoginPromptModal: React.FC = memo(() => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isOpen = useAppSelector(selectShowLoginPrompt);

  const handleClose = useCallback(() => {
    dispatch(closeLoginPrompt());
  }, [dispatch]);

  const handleLogin = useCallback(() => {
    dispatch(closeLoginPrompt());
    navigate(ROUTES.LOGIN, { state: { from: location.pathname } });
  }, [dispatch, navigate, location.pathname]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  }, [handleClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleBackdropClick}
        >
          <motion.div
            className={styles.modal}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <div className={styles.iconWrapper}>
              <FiHeart />
            </div>
            <h2 className={styles.title}>Login Required</h2>
            <p className={styles.description}>
              Please log in to save items to your wishlist. Your wishlist will sync across all your devices.
            </p>
            <div className={styles.actions}>
              <button className={styles.loginBtn} onClick={handleLogin}>
                <FiLogIn />
                Log In
              </button>
              <button className={styles.cancelBtn} onClick={handleClose}>
                Cancel
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

LoginPromptModal.displayName = 'LoginPromptModal';

export default LoginPromptModal;
